import Pagination from "@/components/Pagination";
import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { InlineError } from "@/components/InlineError";
import StatusBadge from "@/components/StatusBadge";
import { Layers, Plus, Archive, Beer, HardHat, Boxes, KeyRound, Store, SlidersHorizontal, Pencil, RotateCcw, Sparkles, Trash2 } from "lucide-react";

const TEMPLATES = [
  { key: "commerce", label: "Commerce / Boutique", icon: Store, desc: "Boissons, riz, farine, épicerie… vente au détail multi-catégories", ready: true },
  { key: "drinks", label: "Dépôt de boissons", icon: Beer, desc: "Sodas, eaux, bières, jus · pack/casier/carton, consignes", ready: true },
  { key: "construction", label: "Matériaux de construction", icon: HardHat, desc: "Ciment, sable, fer, quincaillerie · livraison chantier", ready: true },
  { key: "restaurant", label: "Restauration", icon: Store, desc: "Plats, boissons, ingrédients · caisse rapide", ready: false },
  { key: "auto", label: "Commerce auto / pièces", icon: Boxes, desc: "Pièces, huiles, consommables · compatibilité véhicule", ready: true },
  { key: "rental", label: "Location de matériel", icon: KeyRound, desc: "Actifs, réservations, contrats, cautions", ready: false },
  { key: "services", label: "Services / prestations", icon: Store, desc: "Prestations, caisse, dépenses", ready: false },
  { key: "generic", label: "Activité générique", icon: Boxes, desc: "Modèle vierge, tous les modules", ready: false },
];

export default function Activities() {
  const { activities, reload, access, reloadAccess, settings } = useActivity();
  const { user } = useAuth();
  const isOwner = user?.is_owner || user?.role === "owner";
  const isSuper = user?.role === "superadmin";
  const allModules = access?.all_modules || [];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", template: "commerce", allow_negative_stock: false, currency: "XOF", track_margin: true, stock_mode: "simple", seed_categories: true });
  const [modAct, setModAct] = useState(null);
  const [modMap, setModMap] = useState({});
  const [editAct, setEditAct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [confirmSave, setConfirmSave] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [delLoading, setDelLoading] = useState(false);
  const [delErr, setDelErr] = useState("");

  const askSave = () => {
    setSaveErr("");
    if (!form.name.trim()) { setSaveErr("Le nom de l'activité est obligatoire."); return; }
    setConfirmSave(true);
  };
  const save = async () => {
    setSaving(true); setSaveErr("");
    try {
      await api.post("/activities", form);
      toast.success("Activité créée"); setConfirmSave(false); setOpen(false);
      setForm({ name: "", template: "commerce", allow_negative_stock: false, currency: "XOF", track_margin: true, stock_mode: "simple", seed_categories: true });
      reload(); reloadAccess && reloadAccess();
    } catch (e) { setConfirmSave(false); setSaveErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSaving(false); }
  };
  const archive = async (id) => { try { await api.post(`/activities/${id}/archive`); toast.success("Activité archivée"); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const restore = async (id) => { try { await api.post(`/activities/${id}/restore`); toast.success("Activité restaurée"); reload(); reloadAccess && reloadAccess(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const [delTarget, setDelTarget] = useState(null);
  const doDelete = async () => {
    setDelLoading(true); setDelErr("");
    try { await api.delete(`/activities/${delTarget.id}`); toast.success("Activité archivée (désactivée)"); setDelTarget(null); reload(); reloadAccess && reloadAccess(); }
    catch (e) { setDelErr(apiErr(e.response?.data?.detail, e)); }
    finally { setDelLoading(false); }
  };

  const openEdit = (a) => { setEditErr(""); setEditForm({ id: a.id, name: a.name, template: a.template, currency: a.currency || "XOF", allow_negative_stock: !!a.allow_negative_stock, track_margin: a.track_margin !== false, stock_mode: a.stock_mode || "advanced" }); setEditAct(a); };
  const saveEdit = async () => {
    setEditErr("");
    if (!editForm.name.trim()) { setEditErr("Le nom est obligatoire."); return; }
    setSaving(true);
    try {
      await api.patch(`/activities/${editForm.id}`, { name: editForm.name, template: editForm.template, currency: editForm.currency || "XOF", allow_negative_stock: editForm.allow_negative_stock, track_margin: editForm.track_margin !== false, stock_mode: editForm.stock_mode || "advanced" });
      toast.success("Activité modifiée"); setEditAct(null); reload(); reloadAccess && reloadAccess();
    } catch (e) { setEditErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSaving(false); }
  };

  const [page, setPage] = useState(1);

  const openModules = (a) => {
    setModAct(a);
    const enabled = a.modules || {};
    const map = {};
    allModules.forEach((m) => { map[m.key] = a.modules ? !!enabled[m.key] : true; });
    setModMap(map);
  };
  const saveModules = async () => {
    try {
      await api.put(`/activities/${modAct.id}/modules`, { modules: modMap });
      toast.success("Modules de l'activité mis à jour");
      setModAct(null); reload(); reloadAccess && reloadAccess();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const enabledCount = (a) => a.modules ? Object.values(a.modules).filter(Boolean).length : allModules.length;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Activités</h1>
          <p className="text-sm text-[#64748B] mt-1">Chaque activité est un centre de résultat avec ses propres modules</p></div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button onClick={() => { setSaveErr(""); setForm((f) => ({ ...f, currency: settings?.currency || "XOF" })); setOpen(true); }} className="btn-primary w-full sm:w-auto justify-center" data-testid="new-activity-btn"><Plus className="w-4 h-4" /> Nouvelle activité</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activities.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 card p-6 md:p-8 border-2 border-dashed border-[#BFDBFE] bg-[#F8FAFF]" data-testid="activities-onboarding">
            <div className="flex items-center gap-2 text-[#2563EB] text-[11px] font-bold uppercase tracking-wider mb-2"><Sparkles className="w-4 h-4" /> Bienvenue — créons votre première activité</div>
            <p className="text-sm text-[#475569] max-w-2xl leading-relaxed">Une <b>activité</b> = un commerce que vous gérez (boutique, dépôt de boissons, quincaillerie…). À la création, une seule question détermine la simplicité de votre gestion :</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl border border-[#E2E8F0] bg-white p-4">
                <div className="font-bold font-head text-[#16324F]">🏪 Magasin unique (simple)</div>
                <p className="text-xs text-[#64748B] mt-1 leading-relaxed">Vous avez juste votre magasin. Pas d'entrepôt, pas de choix de point de vente : tout se passe au même endroit. <b>Recommandé pour la plupart des commerces.</b></p>
              </div>
              <div className="rounded-xl border border-[#E2E8F0] bg-white p-4">
                <div className="font-bold font-head text-[#16324F]">🏭 Entrepôt + points de vente (avancé)</div>
                <p className="text-xs text-[#64748B] mt-1 leading-relaxed">Vous gérez un entrepôt central et plusieurs boutiques, avec approvisionnements et suivi du stock par lieu.</p>
              </div>
            </div>
            <button onClick={() => { setSaveErr(""); setForm((f) => ({ ...f, currency: settings?.currency || "XOF" })); setOpen(true); }} className="btn-primary mt-4" data-testid="onboarding-create-activity-btn"><Plus className="w-4 h-4" /> Créer ma première activité</button>
          </div>
        )}
        {activities.slice((page - 1) * 10, page * 10).map((a) => {
          const tpl = TEMPLATES.find((t) => t.key === a.template) || TEMPLATES[4];
          const Icon = tpl.icon;
          return (
            <div key={a.id} className="card p-5" data-testid={`activity-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-lg bg-[#16324F]/5 text-[#16324F] flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                <StatusBadge status={a.status === "archived" ? "archived" : "active"} />
              </div>
              <h3 className="mt-3 font-semibold font-head text-[#16324F]">{a.name}</h3>
              <p className="text-xs text-[#64748B] mt-1">{tpl.label} · <span className="font-medium text-[#2563EB]">{enabledCount(a)} module(s)</span></p>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => openModules(a)} className="btn-access !py-1.5 !px-3" data-testid={`configure-modules-${a.id}`}><SlidersHorizontal className="w-3.5 h-3.5" /> Modules</button>
                <button onClick={() => openEdit(a)} className="btn-edit !py-1.5 !px-3" data-testid={`edit-activity-${a.id}`}><Pencil className="w-3.5 h-3.5" /> Modifier</button>
                {a.status !== "archived"
                  ? <button onClick={() => archive(a.id)} className="btn-archive !py-1.5 !px-3" data-testid={`archive-activity-${a.id}`}><Archive className="w-3.5 h-3.5" /> Archiver</button>
                  : <button onClick={() => restore(a.id)} className="btn-restore !py-1.5 !px-3" data-testid={`restore-activity-${a.id}`}><RotateCcw className="w-3.5 h-3.5" /> Restaurer</button>}
                {isOwner && <button onClick={() => { setDelErr(""); setDelTarget(a); }} className="btn-remove !py-1.5 !px-3" data-testid={`delete-activity-${a.id}`}><Trash2 className="w-3.5 h-3.5" /> Supprimer</button>}
              </div>
            </div>
          );
        })}
      </div>
      <Pagination page={page} setPage={setPage} total={activities.length} />

      <Modal open={open} onClose={() => setOpen(false)} title="Nouvelle activité" testId="activity-modal"
        footer={<><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={askSave} disabled={saving || !form.name.trim()} data-testid="save-activity-btn">Créer l'activité</button></>}>
        <div className="space-y-4">
          <div><label className="lbl">Nom de l'activité</label><input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="activity-name-input" placeholder="Ex : Boutique Zone Nord" /></div>
          <div><label className="lbl">Type d'activité (pré-active les bons modules + catégories de départ)</label>
            <div className="space-y-2">
              {TEMPLATES.filter((t) => t.ready || isSuper).map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.key} onClick={() => setForm({ ...form, template: t.key })} data-testid={`template-${t.key}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${form.template === t.key ? "border-[#2563EB] bg-[#EFF6FF]" : "border-[#E2E8F0] hover:bg-slate-50"}`}>
                    <Icon className={`w-5 h-5 ${form.template === t.key ? "text-[#2563EB]" : "text-[#64748B]"}`} />
                    <div className="flex-1"><div className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">{t.label}{!t.ready && <span className="text-[10px] font-bold uppercase tracking-wide text-[#B45309] bg-[#FEF3C7] px-1.5 py-0.5 rounded">Bientôt (à adapter)</span>}</div><div className="text-xs text-[#64748B]">{t.desc}</div></div>
                  </button>
                );
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#475569]">
            <input type="checkbox" checked={form.allow_negative_stock} onChange={(e) => setForm({ ...form, allow_negative_stock: e.target.checked })} data-testid="allow-negative-checkbox" />
            Autoriser le stock négatif
          </label>
          {isOwner && (
            <label className="flex items-start gap-2 text-sm text-[#475569]">
              <input type="checkbox" className="mt-0.5" checked={form.track_margin !== false} onChange={(e) => setForm({ ...form, track_margin: e.target.checked })} data-testid="activity-track-margin-checkbox" />
              <span>Suivre la marge brute sur cette activité<span className="block text-[11px] text-[#94A3B8]">Décochez pour les activités sans marge produit pertinente (ex : location). Ses produits seront exclus du tableau « Marge par produit ».</span></span>
            </label>
          )}
          <label className="flex items-start gap-2 text-sm text-[#475569]">
            <input type="checkbox" className="mt-0.5" checked={form.stock_mode === "advanced"} onChange={(e) => setForm({ ...form, stock_mode: e.target.checked ? "advanced" : "simple" })} data-testid="activity-stock-mode-checkbox" />
            <span>J'ai un entrepôt séparé de mes points de vente<span className="block text-[11px] text-[#94A3B8]">Cochez si vous gérez un entrepôt central et plusieurs points de vente (mode avancé). Sinon, tout se passe dans un seul magasin — recommandé pour un petit commerce.</span></span>
          </label>
          <div><label className="lbl">Devise</label>
            <select className="inp" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="activity-currency-select">
              <option value="XOF">XOF — Franc CFA (UEMOA)</option>
              <option value="XAF">XAF — Franc CFA (CEMAC)</option>
              <option value="KMF">KMF — Franc comorien</option>
              <option value="EUR">EUR — Euro</option>
              <option value="USD">USD — Dollar US</option>
              <option value="MGA">MGA — Ariary</option>
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm text-[#475569]">
            <input type="checkbox" className="mt-0.5" checked={form.seed_categories !== false} onChange={(e) => setForm({ ...form, seed_categories: e.target.checked })} data-testid="activity-seed-categories-checkbox" />
            <span>Ajouter les catégories suggérées du modèle<span className="block text-[11px] text-[#94A3B8]">Décochez si vous préférez définir vous-même toutes vos catégories (aucune catégorie pré-remplie).</span></span>
          </label>
          <InlineError message={saveErr} title="Création impossible" />
        </div>
      </Modal>

      <ConfirmDialog open={confirmSave} danger={false} title="Confirmer la création" message={`Créer l'activité « ${form.name} » ?`} confirmLabel="Valider et créer" loading={saving} onConfirm={save} onClose={() => setConfirmSave(false)} />

      <Modal open={!!modAct} onClose={() => setModAct(null)} title={`Modules — ${modAct?.name || ""}`} wide testId="activity-modules-modal"
        footer={<><button className="btn-outline" onClick={() => setModAct(null)}>Annuler</button><button className="btn-primary" onClick={saveModules} data-testid="save-activity-modules-btn">Enregistrer les modules</button></>}>
        <div className="space-y-3">
          <p className="text-sm text-[#64748B]">Activez uniquement les modules utilisés par cette activité. Les menus s'adaptent automatiquement pour tous les utilisateurs qui la sélectionnent.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {allModules.map((m) => (
              <label key={m.key} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] cursor-pointer" data-testid={`act-module-${m.key}`}>
                <span className="text-sm">{m.label}</span>
                <input type="checkbox" className="w-4 h-4 accent-[#2563EB]" checked={!!modMap[m.key]} onChange={() => setModMap((mm) => ({ ...mm, [m.key]: !mm[m.key] }))} data-testid={`act-module-checkbox-${m.key}`} />
              </label>
            ))}
          </div>
        </div>
      </Modal>
      <Modal open={!!editAct} onClose={() => setEditAct(null)} title={`Modifier — ${editAct?.name || ""}`} testId="activity-edit-modal"
        footer={<><button className="btn-outline" onClick={() => setEditAct(null)}>Annuler</button><button className="btn-primary" onClick={saveEdit} disabled={saving || !editForm.name?.trim()} data-testid="save-activity-edit-btn">Enregistrer</button></>}>
        <div className="space-y-4">
          <div><label className="lbl">Nom de l'activité</label><input className="inp" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="activity-edit-name-input" /></div>
          <div><label className="lbl">Devise</label>
            <select className="inp" value={editForm.currency || "XOF"} onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })} data-testid="activity-edit-currency-select">
              <option value="XOF">XOF — Franc CFA (UEMOA)</option>
              <option value="XAF">XAF — Franc CFA (CEMAC)</option>
              <option value="KMF">KMF — Franc comorien</option>
              <option value="EUR">EUR — Euro</option>
              <option value="USD">USD — Dollar US</option>
              <option value="MGA">MGA — Ariary</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#475569]">
            <input type="checkbox" checked={!!editForm.allow_negative_stock} onChange={(e) => setEditForm({ ...editForm, allow_negative_stock: e.target.checked })} data-testid="activity-edit-negative-checkbox" />
            Autoriser le stock négatif
          </label>
          {isOwner && (
            <label className="flex items-start gap-2 text-sm text-[#475569]">
              <input type="checkbox" className="mt-0.5" checked={editForm.track_margin !== false} onChange={(e) => setEditForm({ ...editForm, track_margin: e.target.checked })} data-testid="activity-edit-track-margin-checkbox" />
              <span>Suivre la marge brute sur cette activité<span className="block text-[11px] text-[#94A3B8]">Décochez pour les activités sans marge produit pertinente (ex : location).</span></span>
            </label>
          )}
          <label className="flex items-start gap-2 text-sm text-[#475569]">
            <input type="checkbox" className="mt-0.5" checked={editForm.stock_mode === "advanced"} onChange={(e) => setEditForm({ ...editForm, stock_mode: e.target.checked ? "advanced" : "simple" })} data-testid="activity-edit-stock-mode-checkbox" />
            <span>J'ai un entrepôt séparé de mes points de vente<span className="block text-[11px] text-[#94A3B8]">Mode avancé (entrepôt + points de vente). Décochez pour un seul magasin.</span></span>
          </label>
          <InlineError message={editErr} title="Modification impossible" />
        </div>
      </Modal>
      <ConfirmDialog open={!!delTarget} danger title="Supprimer l'activité" message={`Supprimer « ${delTarget?.name || ""} » ? L'activité sera désactivée (archivée) : aucune donnée n'est effacée, l'historique reste conservé pour l'audit et le super-administrateur peut la restaurer.`} confirmLabel="Supprimer (archiver)" loading={delLoading} error={delErr} onConfirm={doDelete} onClose={() => setDelTarget(null)} />
    </div>
  );
}
